<template>
  <div class="certification-version-history">
    <div
      class="certification-version-history__actions"
      :class="{
        'certification-version-history__actions--with-status':
          showCertificationStatus,
      }"
    >
      <span
        v-if="showCertificationStatus"
        class="certification-version-history__status-indicator"
      >
        Certification status: {{ certificationStatusLabel }}
      </span>
      <vs-button
        v-if="canCreateVersions"
        :disabled="isLoading || !latestVersionId"
        class="vs-button vs-button-primary vs-button-border"
        @click="confirmCreateMinorVersion"
      >
        New Exam (Minor)
      </vs-button>
      <vs-button
        v-if="canCreateVersions"
        :disabled="isLoading || !latestVersionId"
        @click="confirmCreateMajorVersion"
      >
        New Version (Major)
      </vs-button>
    </div>
    <content-list-table
      :items="items"
      :typeSelected="typeSelected"
      :pageSize="pageSize"
      :loading="isLoading"
      :columnsByType="columnsByType"
      :cellStylesByType="cellStylesByType"
      :navigateOnSelect="true"
      :editRouteResolver="resolveEditRoute"
    />
    <section
      v-if="certificationChangeNotesEnabled"
      class="certification-version-history__change-notes"
    >
      <h3>Change notes</h3>
      <certification-change-notes-list
        :notes="changeNotes"
        :loading="isChangeNotesLoading"
        :error="changeNotesError"
        :loadMoreError="changeNotesLoadMoreError"
        :hasMore="changeNotesHasMore"
        :loadingMore="isChangeNotesLoadingMore"
        @load-more="loadMoreChangeNotes"
      />
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ContentListTable from './components/ContentListTable.vue'
import { CERT_VERSION_STYLES } from '@/constants/certifications'
import { resolveEditPathByType } from '@/utils/navigationHelper'
import { humanizeStatus } from '@/utils/statusHelper'
import CertificationChangeNotesList from './forms/certifications/components/CertificationChangeNotesList.vue'

export default {
  name: 'certificationVersionHistory',
  components: {
    ContentListTable,
    CertificationChangeNotesList,
  },
  data: () => ({
    typeSelected: 'CertificationVersionHistory',
    certificationId: null,
    pageSize: 25,
    isLoading: false,
    isChangeNotesLoading: false,
    changeNotes: [],
    changeNotesError: '',
    changeNotesLoadMoreError: '',
    changeNotesPage: 1,
    changeNotesHasMore: false,
    isChangeNotesLoadingMore: false,
    changeNotesRequestId: 0,
    cellStylesByType: CERT_VERSION_STYLES,
  }),
  computed: {
    ...mapGetters('featureFlags', ['featureFlagsMap']),
    ...mapGetters('certification', [
      'getCertificationHistory',
      'getCertificationHistoryCertificationId',
      'getCertificationHistoryCertification',
    ]),
    certificationChangeNotesEnabled() {
      return Boolean(
        this.featureFlagsMap && this.featureFlagsMap.certificationChangeNotes
      )
    },
    columnsByType() {
      return {
        CertificationVersionHistory: [
          'version',
          'status',
          'type',
          ...(this.certificationChangeNotesEnabled ? [] : ['comments']),
          'created',
          'modified_by',
          'modified',
        ],
      }
    },
    items() {
      const history =
        this.getCertificationHistoryCertificationId === this.certificationId
          ? this.getCertificationHistory
          : []
      const rows = this.normalizeCertificationVersionHistory(history || [])
      return {
        CertificationVersionHistory: {
          items: rows,
          totalItems: rows.length,
        },
      }
    },
    canCreateVersions() {
      return this.certificationStatus === 'published'
    },
    certificationStatus() {
      const certification = this.getCertificationHistoryCertification
      if (
        this.getCertificationHistoryCertificationId !== this.certificationId ||
        !certification ||
        certification.id !== this.certificationId ||
        !certification.status
      ) {
        return null
      }

      return certification.status
    },
    showCertificationStatus() {
      return (
        this.certificationStatus && this.certificationStatus !== 'published'
      )
    },
    certificationStatusLabel() {
      return humanizeStatus(this.certificationStatus)
    },
    latestVersionId() {
      const latestVersion = this.items.CertificationVersionHistory.items.reduce(
        (currentLatest, item) => {
          if (!item.major_version) return currentLatest
          if (!currentLatest) return item

          return Number(item.major_version) >
            Number(currentLatest.major_version)
            ? item
            : currentLatest
        },
        null
      )
      return latestVersion ? latestVersion.version_id : null
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      async handler(certificationId) {
        if (!certificationId || certificationId === this.certificationId) return
        await this.fetchCertificationVersionHistory(certificationId)
      },
    },
  },
  methods: {
    ...mapActions('certification', [
      'getCertificationHistoryAction',
      'getCertificationChangeNotesAction',
      'createCertificationExamAction',
      'createCertificationVersionAction',
      'setCertificationExamLocalAction',
    ]),
    async fetchCertificationVersionHistory(
      certificationId = this.$route.params.id
    ) {
      const requestId = ++this.changeNotesRequestId
      this.certificationId = certificationId
      this.resetChangeNotes()
      try {
        this.isLoading = true
        const requests = [
          this.getCertificationHistoryAction({
            certificationId,
            includeAll: true,
          }),
        ]
        if (this.certificationChangeNotesEnabled) {
          requests.push(this.fetchChangeNotes(certificationId, requestId))
        }
        await Promise.all(requests)
      } catch (error) {
        if (requestId === this.changeNotesRequestId) this.showError(error)
      } finally {
        if (requestId === this.changeNotesRequestId) this.isLoading = false
      }
    },
    resetChangeNotes() {
      this.changeNotes = []
      this.changeNotesError = ''
      this.changeNotesLoadMoreError = ''
      this.changeNotesPage = 1
      this.changeNotesHasMore = false
      this.isChangeNotesLoading = false
      this.isChangeNotesLoadingMore = false
    },
    async fetchChangeNotes(certificationId, requestId = this.changeNotesRequestId) {
      this.isChangeNotesLoading = true
      this.changeNotesError = ''
      this.changeNotesLoadMoreError = ''
      try {
        const response = await this.getCertificationChangeNotesAction({
          certificationId,
          page: 1,
        })
        if (requestId !== this.changeNotesRequestId) return
        this.changeNotes = response.results || []
        this.changeNotesPage = 1
        this.changeNotesHasMore = !!response.next
      } catch (error) {
        if (requestId === this.changeNotesRequestId)
          this.changeNotesError = 'Unable to load change notes.'
      } finally {
        if (requestId === this.changeNotesRequestId)
          this.isChangeNotesLoading = false
      }
    },
    async loadMoreChangeNotes() {
      if (
        !this.certificationChangeNotesEnabled ||
        this.isChangeNotesLoadingMore ||
        !this.changeNotesHasMore
      )
        return
      const requestId = this.changeNotesRequestId
      const certificationId = this.certificationId
      this.isChangeNotesLoadingMore = true
      this.changeNotesLoadMoreError = ''
      try {
        const response = await this.getCertificationChangeNotesAction({
          certificationId,
          page: this.changeNotesPage + 1,
        })
        if (requestId !== this.changeNotesRequestId) return
        this.changeNotes = [...this.changeNotes, ...(response.results || [])]
        this.changeNotesPage += 1
        this.changeNotesHasMore = !!response.next
      } catch (error) {
        if (requestId === this.changeNotesRequestId)
          this.changeNotesLoadMoreError = 'Unable to load more change notes.'
      } finally {
        if (requestId === this.changeNotesRequestId)
          this.isChangeNotesLoadingMore = false
      }
    },
    async confirmCreateMinorVersion() {
      if (!this.canCreateVersions || !this.latestVersionId) return

      this.openCreateConfirmationDialog(
        this.createMinorVersion,
        'Are you sure you want to create a new minor version?'
      )
    },
    async createMinorVersion() {
      if (!this.canCreateVersions || !this.latestVersionId) return

      try {
        this.isLoading = true
        const response = await this.createCertificationExamAction({
          sourceVersionId: this.latestVersionId,
        })
        const newExamId = response.id
        await this.setCertificationExamLocalAction(response || null)
        this.navigateToCertificationVersionEdit({
          version_id: this.latestVersionId,
          id: newExamId,
          isMinor: true,
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },
    openCreateConfirmationDialog(onAccept, text) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Warning',
        text,
        accept: onAccept,
      })
    },
    navigateToCertificationVersionEdit(row) {
      this.$router.push(this.resolveEditRoute(row))
    },
    async createMajorVersion() {
      if (!this.canCreateVersions || !this.latestVersionId) return

      try {
        this.isLoading = true
        const certificationVersionResponse =
          await this.createCertificationVersionAction({
            sourceVersionId: this.latestVersionId,
          })
        const certificationVersionId =
          certificationVersionResponse.id ||
          certificationVersionResponse.version_id
        const examResponse = await this.createCertificationExamAction({
          sourceVersionId: certificationVersionId,
        })
        const newExamId = examResponse.id
        await this.setCertificationExamLocalAction(examResponse || null)
        this.navigateToCertificationVersionEdit({
          version_id: certificationVersionId,
          id: newExamId,
          isMinor: false,
        })
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },
    async confirmCreateMajorVersion() {
      if (!this.canCreateVersions || !this.latestVersionId) return

      this.openCreateConfirmationDialog(
        this.createMajorVersion,
        'Are you sure you want to create a new major version?'
      )
    },
    resolveEditRoute(row) {
      const name = resolveEditPathByType(this.typeSelected, this.$router)
      if (!name) return {}
      const route = {
        name,
        params: {
          certificationId: this.certificationId,
          certificationVersionId: row.version_id,
        },
      }
      if (row.id) {
        route.query = { examId: row.id }
      }
      if (row.isMinor) {
        route.query = {
          ...route.query,
          tab: 'exam',
        }
      }
      return route
    },
    normalizeCertificationVersionHistory(rows = []) {
      return rows.map((item) => {
        return {
          id: item.exam_id,
          version_id: item.certification_version_id,
          minor_version: item.minor_version,
          isMinor: item.isMinor,
          major_version: item.major_version,
          version: item.version_label,
          status: item.status,
          type: item.isMinor ? 'Minor' : 'Major',
          ...(this.certificationChangeNotesEnabled
            ? {}
            : { comments: item.comments || item.comment || '' }),
          created: item.created,
          modified_by: item.modified_by_name || item.modified_by,
          modified: item.modified,
          metadata: {},
        }
      })
    },
  },
}
</script>

<style scoped>
.certification-version-history {
  padding: 20px;
}

.certification-version-history__actions {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
}

.certification-version-history__actions--with-status {
  justify-content: flex-start;
}
.certification-version-history__change-notes {
  border-bottom: 1px solid #dedede;
  margin: 20px 0;
  padding-bottom: 16px;
}
.certification-version-history__change-notes h3 {
  margin-bottom: 12px;
}

.certification-version-history__status-indicator {
  background: rgba(var(--vs-primary), 0.15);
  border-radius: 0.25rem;
  color: rgba(var(--vs-primary), 1);
  display: inline-block;
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
}
</style>
